import React from 'react';
import { FeeInputs } from '../components/FeeInputs';
import { Loader2 } from 'lucide-react';
import { Bank, BankFee } from '../../../types';

interface BankFeeStepProps {
  banks: Bank[];
  bankFees: BankFee[];
  loading: boolean;
  fees: {
    bankId: string;
    useCustomFees: boolean;
    payments: Record<string, {
      fixedFee: number | '';
      percentageFee: number | '';
      maxCap: number | '';
    }>;
    payouts: Record<string, {
      fixedFee: number | '';
      percentageFee: number | '';
      maxCap: number | '';
    }>;
  };
  onBankChange: (bankId: string) => void;
  onFeesChange: (fees: typeof BankFeeStepProps['fees']) => void;
}

export function BankFeeStep({
  banks,
  bankFees,
  loading,
  fees,
  onBankChange,
  onFeesChange
}: BankFeeStepProps) {
  const handleCustomFeesToggle = (useCustom: boolean) => {
    onFeesChange({
      ...fees,
      useCustomFees: useCustom,
      bankId: useCustom ? '' : fees.bankId
    });
  };

  const handleBankSelection = (bankId: string) => {
    if (!bankId) {
      onFeesChange({
        ...fees,
        bankId: '',
        useCustomFees: true
      });
      onBankChange('');
      return;
    }

    // Get fees for the selected bank
    const bankPaymentFees = bankFees.find(f => 
      f.bank_id === bankId && 
      f.payment_method === 'etransfer_request_money' &&
      f.status === 'approved'
    );
    const bankPayoutFees = bankFees.find(f => 
      f.bank_id === bankId && 
      f.payment_method === 'etransfer_send_money' &&
      f.status === 'approved'
    );

    onFeesChange({
      ...fees,
      bankId,
      useCustomFees: false,
      payments: {
        approved: {
          fixedFee: bankPaymentFees?.per_transaction_fee || 0,
          percentageFee: bankPaymentFees?.rate_fee_percentage || 0,
          maxCap: bankPaymentFees?.max_fee_cap || 0
        },
        declined: {
          fixedFee: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_request_money' &&
            f.status === 'declined'
          )?.per_transaction_fee || 0,
          percentageFee: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_request_money' &&
            f.status === 'declined'
          )?.rate_fee_percentage || 0,
          maxCap: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_request_money' &&
            f.status === 'declined'
          )?.max_fee_cap || 0
        },
        returned: {
          fixedFee: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_request_money' &&
            f.status === 'returned'
          )?.per_transaction_fee || 0,
          percentageFee: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_request_money' &&
            f.status === 'returned'
          )?.rate_fee_percentage || 0,
          maxCap: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_request_money' &&
            f.status === 'returned'
          )?.max_fee_cap || 0
        }
      },
      payouts: {
        approved: {
          fixedFee: bankPayoutFees?.per_transaction_fee || 0,
          percentageFee: bankPayoutFees?.rate_fee_percentage || 0,
          maxCap: bankPayoutFees?.max_fee_cap || 0
        },
        declined: {
          fixedFee: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_send_money' &&
            f.status === 'declined'
          )?.per_transaction_fee || 0,
          percentageFee: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_send_money' &&
            f.status === 'declined'
          )?.rate_fee_percentage || 0,
          maxCap: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_send_money' &&
            f.status === 'declined'
          )?.max_fee_cap || 0
        },
        returned: {
          fixedFee: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_send_money' &&
            f.status === 'returned'
          )?.per_transaction_fee || 0,
          percentageFee: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_send_money' &&
            f.status === 'returned'
          )?.rate_fee_percentage || 0,
          maxCap: bankFees.find(f => 
            f.bank_id === bankId && 
            f.payment_method === 'etransfer_send_money' &&
            f.status === 'returned'
          )?.max_fee_cap || 0
        }
      }
    });
    onBankChange(bankId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convert empty string values to 0 before submitting
    const normalizedFees = {
      ...fees,
      payments: Object.fromEntries(
        Object.entries(fees.payments).map(([status, values]) => [
          status,
          {
            fixedFee: values.fixedFee === '' ? 0 : values.fixedFee,
            percentageFee: values.percentageFee === '' ? 0 : values.percentageFee,
            maxCap: values.maxCap === '' ? 0 : values.maxCap
          }
        ])
      ),
      payouts: Object.fromEntries(
        Object.entries(fees.payouts).map(([status, values]) => [
          status,
          {
            fixedFee: values.fixedFee === '' ? 0 : values.fixedFee,
            percentageFee: values.percentageFee === '' ? 0 : values.percentageFee,
            maxCap: values.maxCap === '' ? 0 : values.maxCap
          }
        ])
      )
    };
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Bank Fee Structure</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Fee Structure
            </label>
            <select
              value={fees.useCustomFees ? 'custom' : (fees.bankId || '')}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  handleCustomFeesToggle(true);
                } else {
                  handleBankSelection(e.target.value);
                }
              }}
              className="mt-1 block w-full"
            >
              <option value="">Select fee structure...</option>
              <option value="custom">Custom Fee Structure</option>
              <optgroup label="Bank Fee Structures">
              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.name} ({bank.alias})
                </option>
              ))}
              </optgroup>
            </select>
          </div>

          {!fees.useCustomFees && fees.bankId && (
            <div className="text-sm text-gray-500">
              <p>
                Using fee structure from selected bank.
              </p>
            </div>
          )}
          {fees.useCustomFees && (
            <div className="text-sm text-gray-500">
              <p>
                Using custom fee structure.
              </p>
            </div>
          )}

          {/* Fee Structure Forms */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Payment Fees */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                Payment Fees - eTransfer Request Money
              </h4>
              <FeeInputs
                fees={fees.payments}
                disabled={!fees.useCustomFees && !!fees.bankId}
                onChange={(newFees) => onFeesChange({
                  ...fees,
                  payments: newFees
                })}
              />
            </div>

            {/* Payout Fees */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-4">
                Payout Fees - eTransfer Send Money
              </h4>
              <FeeInputs
                fees={fees.payouts}
                disabled={!fees.useCustomFees && !!fees.bankId}
                onChange={(newFees) => onFeesChange({
                  ...fees,
                  payouts: newFees
                })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}